import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Role } from "services/rbac/types";
import { usePrivilegesByRole } from "services/rbac/usePrivilegesByRole";

import { PrivilegesTable } from "../PrivilegesTable/PrivilegesTable";
import {
  ALL_SCHEMAS,
  ALL_TABLES,
  ALL_VIEWS,
} from "../PrivilegesTable/constant";
import { ROLES_WIZARD_INIT_DATABASE_PRIVILEGES } from "../privilegesReducer";
import { usePrivilegesState } from "../usePrivilegesContext";
import { getPrivilegesFromRole } from "../utils";
import { SchemasGroup } from "./SchemasGroup";
import { TablesGroup } from "./TablesGroup";
import { ViewsGroup } from "./ViewsGroup";

type Props = {
  catalogName: string;
  role: Role;
};

export const DatabasePrivileges = (props: Props) => {
  const { catalogName, role } = props;
  const { t } = useTranslation();

  const { privilegesDispatch, privilegesState } = usePrivilegesState();
  const privileges = usePrivilegesByRole({
    roleName: role.roleName,
    catalogName,
  });

  const hasPrivileges = privilegesState.databasePrivileges[catalogName];

  useEffect(() => {
    const objectPrivileges = getPrivilegesFromRole({ privileges } as Role);
    const { schema, table, view } = objectPrivileges;
    if (hasPrivileges) {
      return;
    }
    if (view.length === 1 && view[0].objects.length === 0) {
      view[0].objects.push({
        name: ALL_VIEWS,
        catalogName,
      });
    }
    if (table.length === 1 && table[0].objects.length === 0) {
      table[0].objects.push({
        name: ALL_TABLES,
        catalogName,
      });
    }

    if (schema.length === 1 && schema[0].objects.length === 0) {
      schema[0].objects.push({
        name: ALL_SCHEMAS,
        catalogName,
      });
    }

    privilegesDispatch({
      type: ROLES_WIZARD_INIT_DATABASE_PRIVILEGES,
      catalogName,
      schema,
      table,
      view,
    });
  }, [hasPrivileges, catalogName, privileges, privilegesDispatch]);

  const sections = [
    {
      type: "schema",
      title: t("roles_wizard.privileges.column_schemas"),
      Component: SchemasGroup,
      headerAction: true,
    },
    {
      type: "table",
      title: t("roles_wizard.privileges.column_tables"),
      Component: TablesGroup,
      headerAction: true,
    },
    {
      type: "view",
      title: t("roles_wizard.privileges.column_views"),
      Component: ViewsGroup,
      headerAction: true,
    },
  ];

  return (
    <PrivilegesTable
      sections={sections}
      catalogName={catalogName}
    />
  );
};
